import React from 'react';
import {
  Button,
  Dialog,
  CircularProgress,
  Grid,
} from '@nextiva/next-ui-components';
import { string, func, bool } from 'prop-types';
import clsx from 'clsx';

import styles from './NotifyModal.scss';
/* NotifyModal is a re-skinned next-ui modal dialog with two buttons
 * @param {string} mode - pass a mode to change colors. Usable values are in constants/DIALOG_FLAVORS.js
 * @param {string} title - optionally display title in colored header
 * @param {string} cancelText - overwrite button text 'cancel'
 * @param {string} confirmText - overwrite button text 'confirm'
 * @param {function} onCancel - callback for the 'cancel' button
 * @param {function} onConfirm - callback for the 'confirm' button
 * @param {bool} open - when true, the modal will be visible
 * @param {bool} isLoading - when true, disables inputs and displays a spinner
 */
const NotifyModal = ({
  onCancel,
  onConfirm,
  title,
  confirmText,
  cancelText,
  children,
  open,
  isLoading,
  hasCancel,
  mode,
  disabled,
}) => {
  return (
    <Dialog
      className={clsx(styles.dialog, styles[mode])}
      open={open}
      onClose={onCancel}
      maxWidth="xs"
      disableEnforceFocus
    >
      <h4 className={clsx('Heading4', styles.header, styles[mode])}>{title}</h4>
      <div>
        {children}
        <Grid container className={clsx(styles.controls, styles[mode])}>
          <Grid item xs={5}>
            <Button
              onClick={onConfirm}
              className={clsx(styles.primaryButton, styles.buttons)}
              color="primary"
              variant="contained"
              disabled={disabled || isLoading}
              data-testid={`PlatformAdminUI-NotifyModal-${confirmText}-Button`}
            >
              {confirmText}
            </Button>
          </Grid>
          {hasCancel && (
            <Grid item xs={5}>
              <Button
                onClick={onCancel}
                className={styles.buttons}
                color="secondary"
                variant="contained"
                disabled={isLoading}
                id="modal-cancel"
                data-testid={`PlatformAdminUI-NotifyModal-${cancelText}-Button`}
              >
                {cancelText}
              </Button>
            </Grid>
          )}
          {isLoading && <CircularProgress />}
        </Grid>
      </div>
    </Dialog>
  );
};

NotifyModal.propTypes = {
  cancelText: string,
  confirmText: string,
  disabled: bool,
  hasCancel: bool,
  isLoading: bool,
  mode: string,
  open: bool,
  title: string,
  onCancel: func,
  onConfirm: func,
};

NotifyModal.defaultProps = {
  mode: 'info',
  title: '',
  onConfirm: () => {},
  onCancel: () => {},
  cancelText: 'Cancel',
  confirmText: 'Confirm',
  disabled: false,
  open: false,
  isLoading: false,
  hasCancel: true,
};

export default NotifyModal;
